import {
  Autocomplete,
  AutocompleteRenderOptionState,
  Paper,
  Popper,
  PopperPlacementType,
  TextField,
  useTheme,
} from '@mui/material';
import React from 'react';

interface AutocompletePopperProps<T> {
  isOpen: boolean;
  anchorEl?: React.RefObject<HTMLElement | null>;
  closeAutocomplete: () => void;
  label: string;
  placeholder: string;
  options: T[];
  onChange?: (option: T | null) => void;
  getOptionLabel?: (option: T) => string;
  getOptionDisabled?: (option: T) => boolean;
  renderOption?:
    | ((
        props: globalThis.React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
      ) => globalThis.React.ReactNode)
    | undefined;
  multiple?: boolean;
  value?: T;
  removeTags?: boolean;
  placement?: PopperPlacementType;
  loading?: boolean;
}

export const AutocompletePopper = <T,>({
  isOpen,
  anchorEl,
  multiple,
  closeAutocomplete,
  placeholder,
  label,
  options,
  value,
  removeTags,
  onChange,
  renderOption,
  getOptionLabel,
  getOptionDisabled,
  placement,
  loading,
}: AutocompletePopperProps<T>) => {
  const id = isOpen ? 'autocomplete-popper' : undefined;
  const theme = useTheme();

  return (
    <Popper
      id={id}
      open={isOpen}
      anchorEl={anchorEl?.current}
      placement={placement || 'bottom-start'}
      sx={{
        minWidth: '20rem',
        zIndex: theme.zIndex.modal,
      }}
    >
      <Paper square>
        <Autocomplete<T, false, true>
          autoHighlight
          open
          loading={loading}
          disablePortal
          componentsProps={{
            paper: {
              square: true,
            },
          }}
          popupIcon={null}
          renderTags={removeTags ? () => null : undefined}
          renderInput={({ InputProps, inputProps, ...params }) => (
            <TextField
              autoFocus
              {...params}
              InputProps={{
                ...InputProps,
                disableUnderline: true,
                sx: { padding: theme.spacing(1) },
              }}
              inputProps={{
                ...inputProps,
                'aria-label': label,
              }}
              margin="none"
              variant="standard"
              placeholder={placeholder}
            />
          )}
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionDisabled={getOptionDisabled}
          renderOption={renderOption}
          onClose={(_, reason) => {
            if (['escape', 'blur'].includes(reason)) {
              closeAutocomplete();
            }
          }}
          // @ts-expect-error TODO: improve type of value prop
          value={value}
          onChange={(_: unknown, option) => {
            if (typeof onChange === 'function') onChange(option);
            if (!multiple) closeAutocomplete();
          }}
        />
      </Paper>
    </Popper>
  );
};
